import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { BarChart3Icon, ShieldIcon, LeafIcon } from 'lucide-react';
const EMISSIONS_ICON_URL = 'https://cdn.magicpatterns.com/uploads/1YB8RAVfXHcZFM1U9ZZsdQ/line-chart.svg';
export function Features() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section ref={ref} className="w-full bg-[#FAFDF7] py-20 sm:py-28" data-id="element-469">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-470">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-471">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-472">
            Everything you need for
            <span className="text-carbon-bright" data-id="element-473"> carbon management</span>
          </h2>
          <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-474">
            A unified platform to measure, report, and reduce your
            organization's carbon footprint with confidence.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={containerVariants} data-id="element-475">
          {/* Card 1: Emissions Tracking */}
          <motion.article className="p-8 rounded-2xl border border-carbon-geo bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} data-id="element-476">
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-477">
              <BarChart3Icon className="w-6 h-6 text-carbon-bright" data-id="element-478" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-479">
              Emissions Tracking
            </h3>
            <p className="text-carbon-mid leading-relaxed" data-id="element-480">
              Monitor Scope 1, 2, and 3 emissions in real-time across your
              entire value chain. Automated data collection from 500+ sources
              ensures accuracy without manual effort.
            </p>
          </motion.article>

          {/* Card 2: Compliance Reporting */}
          <motion.article className="p-8 rounded-2xl border border-carbon-geo bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} data-id="element-481">
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-482">
              <ShieldIcon className="w-6 h-6 text-carbon-bright" data-id="element-483" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-484">
              Compliance Reporting
            </h3>
            <p className="text-carbon-mid leading-relaxed" data-id="element-485">
              Generate audit-ready reports aligned with GHG Protocol, CSRD, SEC
              climate rules, and ISO 14064. Stay ahead of evolving regulatory
              requirements effortlessly.
            </p>
          </motion.article>

          {/* Card 3: Carbon Offsetting */}
          <motion.article className="p-8 rounded-2xl border border-carbon-geo bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} data-id="element-486">
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-487">
              <LeafIcon className="w-6 h-6 text-carbon-bright" data-id="element-488" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-489">
              Carbon Offsetting
            </h3>
            <p className="text-carbon-mid leading-relaxed" data-id="element-490">
              Access a curated marketplace of verified carbon credits. Track
              retirement, manage portfolios, and demonstrate genuine climate
              action with full transparency.
            </p>
          </motion.article>
        </motion.div>
      </div>
    </section>;
}